/**
 * CaseFeed, the v3 unified feed for a case file.
 *
 * Merges status events from `bill_updates` with chat messages from
 * `bill_communication_log` into a single chronological list. System
 * events render as timeline dots; chat messages render as bubbles
 * with an avatar.
 *
 * Phase 4 of the v3 redesign. Today it's mounted as the "Case Activity"
 * tab inside BillDetailModal; the longer-term plan is to replace the
 * chat-only Dashboard view with this entirely.
 */

import { useMemo } from 'react';
import { Check, Clock, Search, MessageSquare } from 'lucide-react';
import type { BillUpdate, BillCommunicationLog } from '../types/bill';
import '@styles/components/case-feed.css';

type FeedEvent =
  | { kind: 'update'; row: BillUpdate; ts: string }
  | { kind: 'message'; row: BillCommunicationLog; ts: string };

type Props = {
  updates: BillUpdate[];
  commLogs: BillCommunicationLog[];
  /* When true, render shimmer placeholders instead of the empty state —
     prevents the "Nothing here yet" message from flashing during the
     initial Supabase fetch in BillDetailModal. */
  loading?: boolean;
};

/* The status-change update_type carries the strongest visual treatment
   (timeline dot with check / clock icon); the 'info' type renders as
   a quieter inline note. Anything else falls back to 'info' styling. */
function isStatusChange(u: BillUpdate): boolean {
  return u.update_type === 'status_change';
}

/* Direction maps to bubble side. Inbound (from customer) → right-aligned
   ink bubble. Outbound (from specialist) → left-aligned paper bubble. */
function isCustomerMessage(log: BillCommunicationLog): boolean {
  return log.direction === 'inbound';
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
  });
}

export default function CaseFeed({ updates, commLogs, loading }: Props) {
  /* Merge + sort once. New rows arrive via the existing useBillRealtime
     subscription, which pushes them into the parent's state, the parent
     re-renders this component with new props and useMemo recomputes. */
  const feed = useMemo<FeedEvent[]>(() => {
    const merged: FeedEvent[] = [
      ...updates.map((u) => ({ kind: 'update' as const, row: u, ts: u.created_at })),
      ...commLogs.map((c) => ({ kind: 'message' as const, row: c, ts: c.created_at })),
    ];
    /* Newest at top — matches the CasePage feed ordering so the user
       sees what just happened first, regardless of which entry point
       they're viewing the case from. */
    merged.sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
    return merged;
  }, [updates, commLogs]);

  if (loading) {
    return (
      <div className="case-feed-skel" aria-busy="true">
        <div className="case-feed-skel-row" style={{ width: '78%' }} />
        <div className="case-feed-skel-row" style={{ width: '62%' }} />
        <div className="case-feed-skel-row" style={{ width: '88%' }} />
        <div className="case-feed-skel-row" style={{ width: '70%' }} />
      </div>
    );
  }

  if (feed.length === 0) {
    return (
      <div className="case-feed-empty">
        <Clock size={32} className="case-feed-empty-icon" />
        <strong>Nothing here yet.</strong>
        <p>Status updates and messages from your case specialist will appear here.</p>
      </div>
    );
  }

  return (
    <div className="case-feed">
      {feed.map((ev, i) => {
        if (ev.kind === 'update') {
          const u = ev.row;
          const isStatus = isStatusChange(u);
          /* The most recent status event is the "current" state — with
             newest-first sort, that's the first status change we encounter
             walking the feed top-down. Highlight it with fox-faint so the
             user can scan and know where they are. */
          const isLatestStatus =
            isStatus &&
            !feed
              .slice(0, i)
              .some((earlier) => earlier.kind === 'update' && isStatusChange(earlier.row));
          return (
            <div
              key={`u-${u.id}`}
              className={`case-feed-event ${isStatus ? 'is-status' : 'is-info'} ${isLatestStatus ? 'is-current' : ''}`}
            >
              <span className="case-feed-dot">
                {isStatus ? <Check size={11} strokeWidth={3} /> : <Search size={11} />}
              </span>
              <div className="case-feed-event-body">
                <div className="case-feed-event-title">{u.message}</div>
                <div className="case-feed-event-meta num">{formatTime(u.created_at)}</div>
              </div>
            </div>
          );
        }

        const log = ev.row;
        const fromCustomer = isCustomerMessage(log);
        return (
          <div
            key={`m-${log.id}`}
            className={`case-feed-msg ${fromCustomer ? 'is-me' : 'is-specialist'}`}
          >
            {!fromCustomer && (
              <div className="case-feed-avatar" aria-hidden="true">
                <MessageSquare size={14} />
              </div>
            )}
            <div className="case-feed-msg-body">
              <div className="case-feed-msg-author">
                <span className="case-feed-msg-name">
                  {fromCustomer ? 'You' : 'Maya · Case specialist'}
                </span>
                <span className="case-feed-msg-time num">{formatTime(log.created_at)}</span>
              </div>
              <div className="case-feed-bubble">{log.summary}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
